"use client";

import Image from "next/image";
import { useEffect, useState, useSyncExternalStore } from "react";
import { timeRemaining } from "@lib/utils";
import { EpisodeModel, RentalRequestModel, SeriesModel } from "@api/models";
import { useStaticStore, useToast, useUserInfo, useUserPoints } from "app/hooks";
import Dialog from "app/components/Dialog";
import SingleEpisodeRental from "./SingleEpisodeRental";
import CoinAndRental from "./CoinAndRental";
import { STORE_KEYS } from "app/constants";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { generateAuthLinkUrl } from "./AuthLink";
import { RentalOrBuy } from "./RentalOrBuy";
import { logError } from "@lib/logger";
import { sessionStore } from "@lib/sessionStore";
import { PopupWaitFreeIntro } from "./PopupWaitFreeIntro";
import { CoinIcon, FreeTicketIcon } from "./Icons";
import { EpisodeAccessAccessTypeEnum } from "@api/models/EpisodeAccess";
import ChargeProcessing from "./ChargeProcessing";
import { getPaymentIntentInfoFromCookieOnClient } from "@lib/paymentUtils";

export function ChargeBox({ episode, series }: { episode: EpisodeModel; series?: SeriesModel }) {
  const { pushToast } = useToast();

  //要ログインダイアログのopen/close
  const { data: isRequireSigninDialogOpen, mutate: setIsRequireSigninDialogOpen } = useStaticStore(
    STORE_KEYS.requireSigninPopup,
  );

  //購入dialogのopen/close
  const { mutate: setIsPaymentDialogOpen } = useStaticStore(STORE_KEYS.isPaymentDialogOpen);

  //メールアドレス認証dialogのopen/close
  const { mutate: setIsMailConfirmDialogOpen } = useStaticStore(STORE_KEYS.requireMailConfirmPopup);

  //チャージ中dialogのopen/close
  const { mutate: setIsChargeProcessingOpen } = useStaticStore(
    STORE_KEYS.isChargeProcessingDialogOpen,
  );

  //session
  const { sessionStatus } = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.getSnapshot,
    sessionStore.getServerSnapshot,
  );

  //points
  const { userPoints } = useUserPoints();

  //user info
  const { userInfo } = useUserInfo(sessionStatus === "authenticated");

  //loading?
  const [isLoading, setIsLoading] = useState(false);

  //router
  const router = useRouter();

  //url
  const pathname = usePathname();
  const query = useSearchParams();

  const paymentIntentInfoCookie = getPaymentIntentInfoFromCookieOnClient();

  useEffect(() => {
    // ダイアログ閉時に再度開かないよう初回のみ実行
    if (paymentIntentInfoCookie !== null) setIsChargeProcessingOpen(true);
  }, []);

  useEffect(() => {
    if (!isRequireSigninDialogOpen && sessionStatus === "unauthenticated") {
      setIsRequireSigninDialogOpen(true);
    } else {
      //do nothing
    }
  }, [sessionStatus]);

  const handleWaitFreeClick = () => {
    if (sessionStatus === "unauthenticated") {
      router.push(generateAuthLinkUrl(pathname, query, "signin"));
    }
    // 待つと無料かつ待機時間が0の場合の処理
    // waitAndFreeRecoveryTimeがnullの場合は0扱いでＯＫ
    // ※series_wait_users テーブルで待ちの完了時間が保存されていて、待ちが完了次第データが物理削除される=nullもしくは0が待ち時間が完了している状態
    if (episode.access.accessType === "waitAndFree" && !episode.series.waitAndFreeRecoveryTime) {
      setIsLoading(true);
      fetch("/api/checkout/rental", {
        method: "POST",
        body: JSON.stringify({ articleIds: [episode.id] } as RentalRequestModel),
      })
        .then((res) => {
          if (res.status === 401) {
            //未ログインなのでログインページへ遷移
            router.push(generateAuthLinkUrl(pathname, query, "signin"));
          } else if (res.status === 200) {
            window.location.reload();
          } else {
            logError(`api error ${res.status} : checkout wait and free`);
            pushToast(
              `サーバに問題が発生しました(${res.status})。しばらく経ってから再度お試しください`,
              {
                type: "error",
              },
            );
            setIsLoading(false);
          }
        })
        .catch((e) => {
          logError("server error: checkout wait and free");
          pushToast("サーバに問題が発生しました。しばらく経ってから再度お試しください", {
            type: "error",
          });
          setIsLoading(false);
        });
      return;
    }
  };
  let dialog: "coin" | "rental" = "coin";
  if (userPoints && episode.access.price) {
    if (
      (userPoints.paidCoin ?? 0) >= episode.access.price ||
      (!episode.access.isDisableBonus && (userPoints.bonusValue ?? 0) > 0)
    ) {
      // 保有コインが対象の価格以上 or チケット利用可能でチケットを1枚以上保有している
      // 場合はレンタルダイアログを準備
      dialog = "rental";
    }
  }
  const handleRentalClick = (isBulk?: boolean) => {
    if (!episode.access.price) return;
    if (sessionStatus === "unauthenticated") {
      router.push(generateAuthLinkUrl(pathname, query, "signin"));
      return;
    }
    if (!userInfo.isMailAddressVerified) {
      setIsMailConfirmDialogOpen(true);
      return;
    }
    if (isBulk) {
      router.push(`/series/${episode.series.id}/bulk/`);
    } else {
      setIsPaymentDialogOpen(STORE_KEYS.isPaymentDialogOpen, true);
    }
  };

  let header;
  let button = (
    <button type="button" onClick={() => handleRentalClick()} className="ep-charge-act-btn">
      この話を
      <RentalOrBuy />
      する
      <span className="ep-charge-act-btn-price">
        <CoinIcon size={18} className="ep-charge-act-btn-icon" />
        {episode.access.price}
      </span>
    </button>
  );

  const freeTicketButton = (
    <button
      type="button"
      onClick={() => handleRentalClick()}
      className={"ep-charge-act-btn" + (isLoading ? " mode-loading" : "")}
      disabled={isLoading}
    >
      <span>
        「すぐ無料」で読む
        <span className="ep-charge-act-btn-price mode-ticket">
          <Image
            src="/images/icons/free-ticket.svg"
            width={28}
            height={28}
            alt="チケット"
            className="ep-charge-act-btn-icon"
          />
          1枚
        </span>
      </span>
    </button>
  );

  if (episode.access.accessType === EpisodeAccessAccessTypeEnum.WaitAndFree) {
    if (!episode.series.waitAndFreeRecoveryTime) {
      header = "待つと無料で読みますか？";
      button = (
        <button
          type="button"
          onClick={handleWaitFreeClick}
          className={"ep-charge-act-btn mode-waitfree" + (isLoading ? " mode-loading" : "")}
          disabled={isLoading}
        >
          待つと無料で読む
        </button>
      );
    } else {
      const headerBase = (
        <>
          {`あと${timeRemaining(
            episode.series.waitAndFreeRecoveryTime || 0,
          )}後に「待つと無料」で読めます`}
        </>
      );
      let headerSub = (
        <>
          <RentalOrBuy />
          して読みますか？
        </>
      );

      if (userPoints?.bonusValue && !episode.access.isDisableBonus) {
        //チケットOKのエピソード＋チケット持ってる場合
        button = freeTicketButton;
        headerSub = <>「すぐ無料チケット」で読みますか？</>;
      }
      header = (
        <>
          {headerBase}
          <br />
          {headerSub}
        </>
      );
    }
  } else if (episode.access.accessType === "memberOnlyFree") {
    header = (
      <>
        このエピソードは
        <br />
        アカウント登録すると無料で読めます
      </>
    );
    button = (
      <button
        type="button"
        onClick={() => {
          router.push("/auth/signup");
        }}
        className="ep-charge-act-btn"
      >
        無料登録して読む
      </button>
    );
  } else {
    if (userPoints?.bonusValue && !episode.access.isDisableBonus) {
      //チケットOKのエピソード＋チケット持ってる場合
      header = "「すぐ無料チケット」で読みますか？";
      button = freeTicketButton;
    } else {
      header = "この話は有料です";
    }
  }
  return (
    <div className="ep-charge">
      <div className="ep-charge-h">{header}</div>
      <div className="ep-charge-ep">
        <figure className="ep-charge-ep-fig">
          {episode.summary.thumbnailImages && (
            <Image
              src={episode.summary.thumbnailImages[0].url || ""}
              width={episode.summary.thumbnailImages[0].width ?? 0}
              height={episode.summary.thumbnailImages[0].height ?? 0}
              alt=""
              className="ep-charge-ep-img"
            />
          )}
        </figure>
        <span className="ep-charge-ep-h">{episode.summary.title}</span>
      </div>
      <div className="ep-charge-act">
        {button}
        {episode.access.accessType === "memberOnlyFree" && (
          <>
            <span className="ep-charge-act-between-btn-text">登録済みなら</span>
            <button
              type="button"
              onClick={() => router.push("/auth/signin")}
              className="ep-charge-act-btn mode-alt"
            >
              ログインして読む
            </button>
          </>
        )}
        {episode.access.accessType !== "memberOnlyFree" && (
          <button
            type="button"
            onClick={() => handleRentalClick(true)}
            className="ep-charge-act-btn mode-alt"
          >
            残りの話をまとめて
            <RentalOrBuy />
            する
            {series && (
              <span className="ep-charge-act-btn-note">（全{series.summary.numEpisodes}話）</span>
            )}
          </button>
        )}
        {sessionStatus === "authenticated" && (
          <div className="ep-charge-wallet">
            <span className="ep-charge-wallet-h">保有コイン</span>
            <span className="ep-charge-wallet-content">
              <CoinIcon size={18} className="ep-charge-wallet-icon" />
              <span className="ep-charge-wallet-num">{userPoints?.paidCoin}</span>
              +
              <FreeTicketIcon size={18} className="ep-charge-wallet-icon" />
              <span className="ep-charge-wallet-num">{userPoints?.bonusValue ?? 0}</span>
            </span>
          </div>
        )}
      </div>

      {sessionStatus === "authenticated" &&
        (paymentIntentInfoCookie !== null ? (
          <Dialog
            storeKey={STORE_KEYS.isChargeProcessingDialogOpen}
            preventClose={true}
            className="mode-charge"
          >
            <ChargeProcessing />
          </Dialog>
        ) : (
          <Dialog storeKey={STORE_KEYS.isPaymentDialogOpen}>
            {dialog === "rental" && (
              <SingleEpisodeRental
                episodeId={episode.id}
                episodeIndexId={episode.indexId}
                seriesIndexId={episode.series.indexId}
                episodePrice={episode.access.price || 0}
                closeFunction={() => setIsPaymentDialogOpen(false)}
                userBonusTicket={userPoints?.bonusValue ?? 0}
                disableBonus={episode.access.isDisableBonus}
              />
            )}
            {dialog == "coin" && (
              <CoinAndRental
                checkedEpisodes={[
                  {
                    idx: 0,
                    articleHash: episode.id,
                    articleIndexId: episode.indexId,
                    canUseTicket: !episode.access.isDisableBonus,
                    price: episode.access.price ?? 0,
                  },
                ]}
                name={episode.summary.title}
                seriesIndexId={episode.series.indexId}
                // チャージの場合はコインが必須のため必要チケット数を0にする
                totalPrice={episode.access.price || 0}
                totalBonusTicket={0}
                isSingle
                closeFunction={() => {
                  setIsPaymentDialogOpen(false);
                }}
              />
            )}
          </Dialog>
        ))}
      <PopupWaitFreeIntro episode={episode} />
    </div>
  );
}
